import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

/** Per-item sizing for the default 1 / 2 / 3-up responsive layout.
 *  `gap-5` (1.25rem) on the track is subtracted so exactly N cards fill
 *  a row at each breakpoint (no sub-pixel overflow). The `[&>*]` rules
 *  make the wrapped card fill the slot + match the row's tallest card. */
const DEFAULT_ITEM_CLASS =
  'shrink-0 snap-start basis-full sm:basis-[calc((100%_-_1.25rem)/2)] lg:basis-[calc((100%_-_2.5rem)/3)] [&>*]:h-full [&>*]:w-full'

// Sub-pixel tolerance — only treat the track as overflowing (and show
// the controls) when there's a meaningful amount to scroll, so rounding
// on an exactly-fits row doesn't flash the arrows/dots.
const OVERFLOW_THRESHOLD = 8

/**
 * Horizontal, snap-scrolling card slider that only reveals its prev/next
 * arrows + dot indicators when the cards actually overflow the row.
 * When everything fits (few cards / wide screen) it renders as a plain
 * left-aligned row — no controls — so the "grid until it overflows"
 * behaviour comes for free.
 *
 * Pass the cards as children. `itemClass` overrides the per-card slot
 * sizing (must be a static string so Tailwind can see the classes).
 */
export function CardCarousel({
  children,
  itemClass = DEFAULT_ITEM_CLASS,
  ariaLabel = 'Cards',
}) {
  const trackRef = useRef(null)
  const [overflow, setOverflow] = useState(false)
  const [pageCount, setPageCount] = useState(1)
  const [activePage, setActivePage] = useState(0)

  const measure = useCallback(() => {
    const el = trackRef.current
    if (!el || el.clientWidth === 0) return
    const maxScroll = el.scrollWidth - el.clientWidth
    const has = maxScroll > OVERFLOW_THRESHOLD
    setOverflow(has)
    if (!has) {
      setPageCount(1)
      setActivePage(0)
      return
    }
    // One "page" is a viewport-width scroll; the +1 counts the first,
    // un-scrolled view.
    const pages = Math.ceil(maxScroll / el.clientWidth) + 1
    setPageCount(pages)
    // Map scrollLeft → active page. The pages are whole-viewport steps
    // (0, 1×, 2× … clientWidth) with the LAST page pinned to maxScroll,
    // which is usually a PARTIAL step (6 cards at 3-up overflows by ~1.3
    // viewports → stops at 0, 1.0, 1.3). The earlier progress-based map
    // (scrollLeft / maxScroll × (pages−1)) assumed EVENLY spaced stops,
    // so a single one-viewport scroll sat at progress ≈0.77 and rounded
    // to the LAST dot — making the Next arrow jump first → last and skip
    // the middle. Round by whole viewports instead; and because the
    // final (partial) step rounds short of its index, treat "within 1px
    // of the end" as the last page so the end dot still lights up (this
    // also covers a tiny <1-viewport overflow, where the end would
    // otherwise round back to page 0).
    const atEnd = el.scrollLeft >= maxScroll - 1
    setActivePage(
      atEnd
        ? pages - 1
        : Math.min(
            pages - 1,
            Math.max(0, Math.round(el.scrollLeft / el.clientWidth)),
          ),
    )
  }, [])

  const childArray = Array.isArray(children)
    ? children.filter(Boolean)
    : [children].filter(Boolean)
  const count = childArray.length

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    el.addEventListener('scroll', measure, { passive: true })
    return () => {
      ro.disconnect()
      el.removeEventListener('scroll', measure)
    }
  }, [measure, count])

  function scrollByPage(dir) {
    const el = trackRef.current
    if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
  }
  function goToPage(i) {
    const el = trackRef.current
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }

  return (
    <div role="region" aria-roledescription="carousel" aria-label={ariaLabel}>
      {/* Track — `pt-4` gives the cards' top-border "Most Popular"
       *  ribbon room inside the overflow clip (overflow-x:auto forces
       *  overflow-y to auto, which would otherwise crop it).
       *
       *  `snap-proximity` (NOT `mandatory`): when the last "page" is a
       *  partial one (e.g. 6 cards at 3-up = one-and-a-bit pages), a
       *  mandatory snap force-jumps a manual scroll/drag straight to the
       *  final card instead of moving card-by-card. Proximity only aligns
       *  when the user lands NEAR a card edge, so the row scrolls
       *  smoothly and incrementally. */}
      <div
        ref={trackRef}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-proximity pt-4 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {childArray.map((child, i) => (
          <div key={i} className={itemClass}>
            {child}
          </div>
        ))}
      </div>

      {overflow ? (
        <div className="mt-5 flex items-center justify-center gap-4">
          <ArrowButton
            direction="prev"
            disabled={activePage === 0}
            onClick={() => scrollByPage(-1)}
          />
          <div className="flex items-center gap-2">
            {Array.from({ length: pageCount }).map((_, i) => {
              const active = i === activePage
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => goToPage(i)}
                  className={
                    'h-2 rounded-full transition-all cursor-pointer ' +
                    (active
                      ? 'w-6 bg-[color:var(--color-accent-strong)]'
                      : 'w-2 bg-[color:var(--color-rule-strong)] hover:bg-[color:var(--color-ink-subtle)]')
                  }
                />
              )
            })}
          </div>
          <ArrowButton
            direction="next"
            disabled={activePage >= pageCount - 1}
            onClick={() => scrollByPage(1)}
          />
        </div>
      ) : null}
    </div>
  )
}

function ArrowButton({ direction, disabled, onClick }) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous plans' : 'Next plans'}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink-muted)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
    >
      <Icon size={18} />
    </button>
  )
}
